import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Company,
  GetParams,
  GetResponse,
  PostResponse,
  UserCompany,
} from '@app/shared';
import { BehaviorSubject, Observable } from 'rxjs';
import { convertGetParamsToHttpParams } from '../utils/api-utils';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  selectedCompanyId$: BehaviorSubject<number | undefined> = new BehaviorSubject<
    number | undefined
  >(undefined);

  selectedCompanyIds$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);

  private lastNotificationTime: number = 0;
  private readonly NOTIFICATION_COOLDOWN = 5000; // 5 segundos entre notificaciones

  /** URLs principales relacionadas con Company **/
  companyUrl = environment.identityApiBaseUrl + '/api/company';
  userCompanyUrl = environment.identityApiBaseUrl + '/api/user-company';

  constructor(
    private http: HttpClient,
    private notificationService: NzNotificationService
  ) {}

  get selectedCompanyId(): Observable<number | undefined> {
    return this.selectedCompanyId$ as Observable<number | undefined>;
  }

  set selectedCompanyId(value: number) {
    this.selectedCompanyId$.next(value);
  }

  get selectedCompanyIds(): number[] {
    return this.selectedCompanyIds$.value;
  }

  set selectedCompanyIds(ids: number[]) {
    this.selectedCompanyIds$.next(ids);
  }

  /**
   * Compañía donde se está operando (para crear/editar).
   * Es independiente del filtro de compañías (selectedCompanyIds).
   */
  set operatingCompanyId(id: number) {
    this.selectedCompanyId$.next(id);
  }

  get operatingCompanyId(): number | undefined {
    return this.selectedCompanyId$.getValue();
  }

  /** Metodos de obtener/crear/actualizar/eliminar Company **/
  getCompany(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.companyUrl, {
      params: convertGetParamsToHttpParams(getParams),
    });
  }

  createCompany(newCompany: any | FormData): Observable<PostResponse> {
    // Si es FormData, no establecer Content-Type (el navegador lo hará automáticamente con el boundary)
    if (newCompany instanceof FormData) {
      return this.http.post<PostResponse>(this.companyUrl, newCompany);
    }
    // Si es un objeto normal, enviar como JSON
    return this.http.post<PostResponse>(this.companyUrl, newCompany);
  }

  updateCompany(Company: Company | FormData): Observable<any> {
    // Si es FormData, no establecer Content-Type (el navegador lo hará automáticamente con el boundary)
    if (Company instanceof FormData) {
      return this.http.put(this.companyUrl, Company);
    }
    // Si es un objeto normal, enviar como JSON
    return this.http.put(this.companyUrl, Company);
  }

  deleteCompany(ids: number[]): Observable<any> {
    return this.http.delete(this.companyUrl, { body: { ids: ids } });
  }

  /** Metodos de obtener/crear/actualizar/eliminar UserCompany **/
  getUserCompany(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.userCompanyUrl, {
      params: convertGetParamsToHttpParams(getParams),
    });
  }

  createUserCompany(newUserCompany: {
    name: string;
    isActive: boolean;
  }): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.userCompanyUrl, newUserCompany);
  }

  updateUserCompany(UserCompany: UserCompany): Observable<any> {
    return this.http.put(this.userCompanyUrl, UserCompany);
  }

  deleteUserCompany(desconsolidationsTypeIds: number[]): Observable<any> {
    return this.http.delete(this.userCompanyUrl, {
      body: { ids: desconsolidationsTypeIds },
    });
  }

  public getCompanyHeaders(): HttpHeaders {
    // El header companyId siempre envía la compañía donde se opera (un solo ID).
    // El filtro multi-compañía se envía en el body de los endpoints que lo soportan.
    const companyId = this.selectedCompanyId$.getValue();

    if (!companyId) {
      // Mostrar notificación solo si han pasado al menos 5 segundos desde la última
      const now = Date.now();
      if (now - this.lastNotificationTime > this.NOTIFICATION_COOLDOWN) {
        this.lastNotificationTime = now;
        // Usar warning con título y contenido
        this.notificationService.warning(
          'Compañía no seleccionada',
          'Por favor, seleccione una compañía para continuar.',
          {
            nzPlacement: 'bottomRight',
            nzDuration: 3000,
            nzClass: 'custom-notification',
          }
        );
      }

      // Retornar headers sin companyId en lugar de lanzar error
      return new HttpHeaders({
        accept: 'application/json',
        'Content-Type': 'application/json',
      });
    }

    return new HttpHeaders({
      companyId: companyId.toString(),
      accept: 'application/json',
      'Content-Type': 'application/json',
    });
  }
}
